import React, { useState, useEffect, useCallback } from 'react';
import { HiTruck, HiCheckCircle } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import { getDeliveries, updateDelivery } from '../../services/api';
import { format } from 'date-fns';

const STATUS_OPTS = ['scheduled', 'in_transit', 'delivered', 'failed', 'rescheduled'];

const statusBadge = s => {
  const map = { scheduled: 'badge-blue', in_transit: 'badge-purple', delivered: 'badge-green', failed: 'badge-red', rescheduled: 'badge-yellow' };
  return <span className={`badge ${map[s] || 'badge-gray'}`}>{s.replace('_', ' ')}</span>;
};

export default function StaffDeliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', date: format(new Date(), 'yyyy-MM-dd') });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, ...filters };
      if (!params.status) delete params.status;
      if (!params.date) delete params.date;
      const { data } = await getDeliveries(params);
      setDeliveries(data.data);
      setTotal(data.total);
    } catch {
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, status) => {
    setUpdating(id);
    try {
      await updateDelivery(id, { status });
      toast.success('Status updated');
      load();
    } catch {
      toast.error('Failed to update');
    } finally {
      setUpdating(null);
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="content-area">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Deliveries</h1>
          <p className="text-muted">{total} deliveries found</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body" style={{ paddingBottom: '1rem' }}>
          <div className="filter-bar">
            <div>
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.date}
                onChange={e => { setFilters(f => ({ ...f, date: e.target.value })); setPage(1); }}
                style={{ width: 160 }}
              />
            </div>
            <div>
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={filters.status}
                onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
                style={{ width: 160 }}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div style={{ marginTop: '1.4rem' }}>
              <button className="btn btn-secondary" onClick={() => { setFilters({ status: '', date: '' }); setPage(1); }}>Clear</button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : deliveries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
            <HiTruck style={{ fontSize: '3rem', display: 'block', margin: '0 auto 0.75rem' }} />
            No deliveries found.
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Scheduled</th>
                  <th>Status</th>
                  <th>Update</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map(d => (
                  <tr key={d.id}>
                    <td><span className="badge badge-blue">{d.order_number}</span></td>
                    <td><strong>{d.customer_name}</strong></td>
                    <td>{d.customer_phone}</td>
                    <td className="text-muted" style={{ maxWidth: 180, fontSize: '0.8125rem' }}>{d.customer_address}</td>
                    <td>{d.scheduled_date ? format(new Date(d.scheduled_date), 'dd MMM yyyy') : '-'}</td>
                    <td>{statusBadge(d.status)}</td>
                    <td>
                      {d.status !== 'delivered' && (
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          {d.status === 'scheduled' && (
                            <button
                              className="btn btn-sm"
                              style={{ background: '#ede9fe', color: '#7c3aed' }}
                              disabled={updating === d.id}
                              onClick={() => handleStatusChange(d.id, 'in_transit')}
                            >
                              <HiTruck /> Start
                            </button>
                          )}
                          {d.status === 'in_transit' && (
                            <button
                              className="btn btn-success btn-sm"
                              disabled={updating === d.id}
                              onClick={() => handleStatusChange(d.id, 'delivered')}
                            >
                              <HiCheckCircle /> Delivered
                            </button>
                          )}
                          <select
                            className="form-control"
                            style={{ width: 140, padding: '0.3rem 0.5rem', fontSize: '0.8rem' }}
                            value={d.status}
                            disabled={updating === d.id}
                            onChange={e => handleStatusChange(d.id, e.target.value)}
                          >
                            {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                          </select>
                        </div>
                      )}
                      {d.status === 'delivered' && (
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                          {d.completed_at ? format(new Date(d.completed_at), 'HH:mm') : 'Done'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div style={{ padding: '1rem 1.5rem' }}>
            <div className="pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
                <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
